
import { mapGetters } from 'vuex'
import CommunityCardGrid from '../../components/community/CommunityCardGrid.vue'
import ScrollToTop from '../../components/community/ScrollToTop.vue'
import MachinationsLightPage from '~/domain/pages/MachinationsLightPage'
import {
  Diagram,
  UserType,
  COMMUNITY_FILTERS,
  DiagramStoreActions,
  DesignersStoreActions,
} from '~/domain/interfaces'
import {
  isSeeAllFilter,
  isDesignersFilter,
  isDiagramsFilter,
} from '~/domain/helpers'
import CommunityCardCarousel from '~/components/community/CommunityCardCarousel.vue'
import CommonSearchBar from '~/components/common/CommonSearchBar.vue'

export default MachinationsLightPage.extend({
  layout: 'communityLayout',
  head() {
    const filter: string = this.filter || ''
    // @ts-ignore
    const pageHead = this.pageHead()
    const urlPageNo = parseInt(this.$route.query?.page?.toString()) || 0
    const pageString = `${urlPageNo > 1 ? ` • page ${urlPageNo}` : ''}`
    const title =
      filter.includes(COMMUNITY_FILTERS.DIAGRAMS) ||
      filter.includes(COMMUNITY_FILTERS.NEW_TEMPLATES)
        ? 'Community Designed Systems' + pageString
        : filter.includes(COMMUNITY_FILTERS.DESIGNERS)
        ? 'View Designer Profiles' + pageString
        : 'View Recent Activity - Design Community'
    const description = filter.includes(COMMUNITY_FILTERS.DESIGNERS)
      ? `Explore community designer profiles. Unlock new insights and connect with like-minded professionals. ${pageString}`
      : `Explore what our community designers are crafting. Unlock new insights and connect with like-minded professionals. ${pageString}`
    return {
      ...pageHead,
      title,
      meta: [
        {
          hid: 'og:title',
          name: 'og:title',
          content: title,
        },
        {
          hid: 'description',
          name: 'description',
          content: description,
        },
        {
          hid: 'og:description',
          name: 'og:description',
          content: description,
        },
        ...pageHead?.meta,
      ],
      link: [
        {
          rel: 'canonical',
          href: this.$config?.nuxtBaseURL + this.$route.fullPath,
        },
      ],
      bodyAttrs: {
        class: 'see-all-page',
      },
    }
  },
  asyncData(context) {
    const { query, error, route } = context
    const filter = query.filter || ''

    // If the filter parameter is invalid, display the 404 error page.
    if (!filter || filter?.length === 0 || !isSeeAllFilter(filter as string)) {
      return error({
        statusCode: 404,
        message: `Invalid route: ${route.fullPath}`,
      })
    }
  },
  async fetch() {
    const { $store } = this

    await $store.dispatch('common/setupPageLight', { context: this })

    this.querySearchTerm = (this.$route.query.q as string) ?? ''
    this.showStopScrollCTA = false

    await this.getFilteredResults(false).then(() => {
      this.computeColumnsData()
      this.computeCssData()
    })
  },
  data() {
    return {
      diagrams: [] as Diagram[],
      totalDiagrams: 0,
      designers: [] as UserType[],
      totalDesigners: 0,
      pageNo: 0,
      loading: false,
      source: '',
      querySearchTerm: '',
      isSearchFocused: false,
      complete: false,
      currentData: {} as any,
      dataCTA: {},
      showCTACard: false,
      hiddenItems: [] as any,
      showStopScrollCTA: false,
      gridColumnCount: 0,
      gridColumnGap: '',
      gridRowHeight: '',
      gridRowWidth: '',
      showHighlightedItems: false,
      highlightedItemsData: {} as any,
      highlightedItemsHeight: '' as string,
    }
  },
  computed: {
    ...mapGetters({
      userIsAuthenticated: 'userIsAuthenticated',
    }),
    filter(): string {
      return typeof this.$route.query.filter === 'string'
        ? this.$route.query.filter
        : ''
    },
    tagId() {
      return this.$route.query.tagId || ''
    },
    sortingCategory(): string {
      return typeof this.$route.query.s === 'string' ? this.$route.query.s : ''
    },
    filteringCategory(): string {
      return typeof this.$route.query.f === 'string' ? this.$route.query.f : ''
    },
    isDiagramsFilter(): boolean {
      return isDiagramsFilter((this.filter as string) || '')
    },
    isDesignersFilter(): boolean {
      return isDesignersFilter((this.filter as string) || '')
    },
    filteredResults(): any {
      return this.currentData.results
    },
  },
  watch: {
    '$route.query': '$fetch',
  },
  created() {
    if (this.$route.query && this.$route.query.page) {
      /* Substract 1 because pageNo is incremented in getDiagrams/getDesigners */
      this.pageNo = parseInt(this.$route.query.page.toString()) - 1
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.computeColumnsData()
      this.computeCssData()
    })

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => {
        this.computeColumnsData()
        this.computeCssData()
      })
    }
  },
  destroyed() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', () => {
        this.computeColumnsData()
        this.computeCssData()
      })
    }
  },
  methods: {
    async getFilteredResults(incrementPageNo = true) {
      this.loading = true
      incrementPageNo ? this.pageNo++ : (this.pageNo = 0)

      try {
        const isDiagramsPage = this.filter === COMMUNITY_FILTERS.DIAGRAMS
        const isDesignersPage = this.filter === COMMUNITY_FILTERS.DESIGNERS
        const userIsAuthenticated = this.$store.getters.userIsAuthenticated
        this.showCTACard =
          !this.querySearchTerm &&
          !this.tagId &&
          (isDiagramsPage || isDesignersPage) &&
          this.pageNo <= 1
        this.showHighlightedItems =
          !this.querySearchTerm &&
          !this.tagId &&
          (isDiagramsPage || isDesignersPage)

        const seeAll = await this.$store.dispatch(
          'community/getSeeAllResults',
          {
            context: this,
            params: {
              filter: this.filter,
              searchTerm: this.querySearchTerm,
              tagId: this.tagId,
              pageNo: this.pageNo,
            },
          }
        )

        if (
          seeAll &&
          Array.isArray(seeAll?.results) &&
          seeAll.results.length > 0
        ) {
          // Add 'CTA' card on first fetch if display conditions are met

          if (this.showCTACard) {
            let ctaTitle, ctaSubtitle, ctaButtonText
            const authenticatedUser = this.$store.getters.authenticatedUser

            if (isDiagramsPage) {
              let userDiagrams

              // Check if authenticated user has diagrams in community
              if (userIsAuthenticated) {
                userDiagrams = await this.$store.dispatch(
                  'diagrams/getDiagramsForUser',
                  {
                    context: this,
                    username: authenticatedUser.username,
                    count: 1,
                    page: 1,
                  }
                )
              }

              if (
                !userIsAuthenticated ||
                (userIsAuthenticated && userDiagrams?.totalCount === 0)
              ) {
                ctaTitle = this.$t('diagramCTACardTitle1')
                ctaSubtitle = this.$t('diagramCTACardSubtitle1')
              } else {
                ctaTitle = this.$t('diagramCTACardTitle2')
                ctaSubtitle = this.$t('diagramCTACardSubtitle2')
              }
            } else if (isDesignersPage) {
              let hasCompleteAccount = false

              // Check if the authenticated user's profile is complete
              if (authenticatedUser) {
                hasCompleteAccount =
                  authenticatedUser?.hasCompleteAccount || false
              }

              if (userIsAuthenticated && !hasCompleteAccount) {
                ctaTitle =
                  this.$t('designerCTACardTitle1') +
                  ' ' +
                  authenticatedUser?.username
                ctaSubtitle = this.$t('designerCTACardSubtitle1')
                ctaButtonText = this.$t('designerCTACardButtonText1')
              } else if (!userIsAuthenticated) {
                ctaTitle = this.$t('designerCTACardTitle2')
                ctaSubtitle = this.$t('designerCTACardSubtitle2')
                ctaButtonText = this.$t('designerCTACardButtonText2')
              } else {
                this.showCTACard = false
              }
            }

            if (this.showCTACard) {
              this.hiddenItems.push(seeAll.results.pop())
              seeAll.results.unshift({
                showCTACard: true,
                dataCTA: {
                  title: ctaTitle,
                  subtitle: ctaSubtitle,
                  buttonText: ctaButtonText,
                },
              })
            }
          }

          if (this.pageNo > 1) {
            let previousData = Array.isArray(this.currentData?.results)
              ? this.currentData.results
              : []

            if (this.showCTACard && this.hiddenItems.length > 0) {
              previousData = [...previousData, this.hiddenItems.shift()]
              this.hiddenItems.push(seeAll.results.pop())
            }

            this.currentData = {
              ...seeAll,
              results: previousData.concat(seeAll.results),
            }
          } else {
            this.currentData = {
              ...seeAll,
            }
          }

          // todo: remove comment after sitemaps are populated with all community links
          // if (
          //   !this.userIsAuthenticated &&
          //   this.pageNo === 5 &&
          //   this.currentData.results.length < this.currentData.totalResults
          // ) {
          //   this.computeColumnsData()
          //   this.complete = true
          //   this.showStopScrollCTA = true
          // } else {
          this.complete =
            this.currentData.results.length === this.currentData.totalResults
          // }

          if (this.showHighlightedItems) {
            let highlightedItems = []

            if (isDiagramsPage) {
              highlightedItems = await this.$store.dispatch(
                DiagramStoreActions.GET_DIAGRAMS_OF_THE_MONTH,
                this
              )
            } else if (isDesignersPage) {
              await this.$store.dispatch(
                DesignersStoreActions.FETCH_FEATURED,
                this
              )
              highlightedItems = this.$store.getters['designers/featured']
            }

            this.highlightedItemsData = {
              title: isDiagramsPage
                ? this.$t('diagramsOfTheMonth')
                : this.$t('designersOfTheMonth'),
              subtitle: isDiagramsPage
                ? this.$t('diagramsOfTheMonthSubtitle')
                : this.$t('designersOfTheMonthSubtitle'),
              items: highlightedItems,
              type: isDiagramsPage ? 'diagram' : 'designer',
            }
          }
        } else if (seeAll.totalResults === 0) {
          this.currentData = {
            ...seeAll,
          }
        }

        this.loading = false
      } catch (err) {
        this.loading = false
      }
    },
    handleSearch() {
      this.pageNo = 0
      this.showHighlightedItems = false
    },
    updateQuery(params) {
      const updatedQuery = { ...this.$route.query, ...params }

      Object.keys(params).forEach((key) => {
        if (
          params[key] === null ||
          params[key] === undefined ||
          params[key]?.length === 0
        ) {
          delete updatedQuery[key]
        }
      })

      this.$router.push({ query: updatedQuery })
    },
    computeColumnsData() {
      const cardGrid = (this.$refs.cardGrid as Vue)?.$el as HTMLElement
      if (cardGrid) {
        const cardGridComputedStyles = window.getComputedStyle(cardGrid)
        const card = cardGrid.getElementsByClassName(
          'm-diagram-card-grid-view'
        )[2]
        if (card) {
          const cardComputedStyles = window.getComputedStyle(card)
          this.gridColumnCount = cardGridComputedStyles
            .getPropertyValue('grid-template-columns')
            .split(' ').length
          this.gridColumnGap =
            Math.max(
              parseInt(
                cardGridComputedStyles.getPropertyValue('width').split('px')[0]
              ) * 0.01,
              16
            ) + 'px'
          this.gridRowHeight = cardComputedStyles.getPropertyValue('height')
          this.gridRowWidth = cardComputedStyles.getPropertyValue('width')
        }
      }
    },
    computeCssData() {
      /* Highlighted items height */
      const headerElement = this.$refs.header as HTMLElement

      if (headerElement) {
        const headerElementComputedStyles =
          window.getComputedStyle(headerElement)
        this.highlightedItemsHeight =
          parseInt(
            headerElementComputedStyles
              .getPropertyValue('height')
              .split('px')[0]
          ) +
          81 +
          'px'
      }
    },
  },
  components: {
    CommunityCardGrid,
    ScrollToTop,
    CommunityCardCarousel,
    CommonSearchBar,
  },
})
